import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import FormCheck from 'react-bootstrap/FormCheck';
import Row from 'react-bootstrap/Row';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { userActions } from '../../actions';
import HeadOverride from '../../components/general/HeadOverride';
import LoadingImage from '../../components/general/LoadingImage';
import useDispatch from '../../hooks/useDispatch';
import useSelector from '../../hooks/useSelector';
import { ThirdPartyLoginConnection } from '../../py-generated/datalouder/third_party_login/types';
import { ROUTE_PROFILE } from '../RouteConstants';
import ThirdPartyLoginButtons from './ThirdPartyLogin/ThirdPartyLoginButtons';

const Styles = styled.div`
  margin-top: 3rem;
`;

const ThirdPartyLoginContainer = styled.div`
  margin-top: 1rem;
`;

export const renderPasswordFields = (
  password: string,
  setPassword: (password: string) => void,
  passwordRetype: string,
  setPasswordRetype: (password: string) => void,
  submitted: boolean,
  t: TFunction
): React.ReactNode => {
  return (
    <div>
      <Form.Group className="form-group" controlId="formBasicPassword">
        <Form.Label>{t('Password')}</Form.Label>
        <Form.Control
          type="password"
          placeholder={t('Password')}
          name="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
        />
        {submitted && !password && (
          <Form.Text className="text-danger">
            {t('Password is required.')}
          </Form.Text>
        )}
      </Form.Group>

      <Form.Group className="form-group" controlId="formBasicPassword">
        <Form.Label>{t('Retype Password')}</Form.Label>
        <Form.Control
          type="password"
          placeholder={t('Password')}
          name="passwordRetype"
          value={passwordRetype}
          onChange={e => setPasswordRetype(e.target.value)}
        />

        {submitted && !passwordRetype && (
          <Form.Text className="text-danger">
            {t('Password is required.')}
          </Form.Text>
        )}

        {submitted && password !== passwordRetype && (
          <Form.Text className="text-danger">
            {t('Please retype your password.')}
          </Form.Text>
        )}
      </Form.Group>
    </div>
  );
};

interface Props {
  fullWidth?: boolean;
}

export interface LocationState {
  from?: string;
  thirdParty?: {
    provider: ThirdPartyLoginConnection['provider'];
    profile: ThirdPartyLoginConnection['third_party_user_profile'];
    token?: string;
  };
}

const RegisterPage: React.FC<Props> = ({ fullWidth }) => {
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const thirdPartyState = locationState?.thirdParty;
  const isThirdPartyRegister = !!thirdPartyState;
  const requiringPassword = !isThirdPartyRegister;

  const [t] = useTranslation();

  const [email, setEmail] = useState('');
  const [alias, setAlias] = useState('');
  const [publicName, setPublicName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordRetype, setPasswordRetype] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const trimmedEmail = email.trim();

  const { registered, loading, user } = useSelector(state => state.user);

  const dispatch = useDispatch();

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      setSubmitted(true);
      if (trimmedEmail && password && alias && agreed && publicName) {
        dispatch(
          userActions.register(
            {
              alias,
              login: trimmedEmail,
              password,
              publicName,
            },
            thirdPartyState?.provider,
            thirdPartyState?.token
          )
        );
      }
    },
    [
      trimmedEmail,
      password,
      alias,
      agreed,
      publicName,
      dispatch,
      thirdPartyState?.provider,
      thirdPartyState?.token,
    ]
  );

  const navigate = useNavigate();

  const from = (location.state as LocationState)?.from;

  useEffect(() => {
    if (registered) {
      // Redirect to profile page after registration unless there is a `from` state

      if (from) {
        return navigate(from);
      }
      navigate(ROUTE_PROFILE);
    } else if (user) {
      // already logined
      navigate('/payment');
    }
  }, [from, navigate, registered, user]);

  useEffect(() => {
    if (thirdPartyState) {
      setAlias(thirdPartyState?.profile.name);
      setPublicName(thirdPartyState?.profile.name);
      if (thirdPartyState?.profile.email) {
        setEmail(thirdPartyState?.profile.email);
      }
      setPassword(uuidv4() + 'A0'); // a random init password and users won't need it
    }
  }, [
    isThirdPartyRegister,
    thirdPartyState,
    thirdPartyState?.profile.email,
    thirdPartyState?.profile.name,
    thirdPartyState?.provider,
  ]);

  if (registered || user) {
    return null;
  }

  return (
    <Container>
      <HeadOverride>Register</HeadOverride>
      <Row>
        <Col md={{ span: fullWidth ? 12 : 6, offset: fullWidth ? 0 : 3 }}>
          <Styles>
            <Card>
              <Card.Header>{t('Register')}</Card.Header>
              <Card.Body>
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="form-group" controlId="formBasicEmail">
                    <Form.Label>{t('Your name')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('How you want us to call you?')}
                      name="alias"
                      value={alias}
                      onChange={e => setAlias(e.target.value)}
                    />
                    {submitted && !alias && (
                      <Form.Text className="text-danger">
                        {t('Name is required.')}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="form-group" controlId="formBasicEmail">
                    <Form.Label>{t('Your public name')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t(
                        'Public name will be used in watching list'
                      )}
                      name="publicName"
                      value={publicName}
                      onChange={e => setPublicName(e.target.value)}
                    />
                    {submitted && !publicName && (
                      <Form.Text className="text-danger">
                        {t('Public Name is required')}
                      </Form.Text>
                    )}
                  </Form.Group>

                  <Form.Group className="form-group" controlId="formBasicEmail">
                    <Form.Label>
                      {t('Email address or phone number')}
                    </Form.Label>
                    <Form.Control
                      placeholder={t('Login placeholder')}
                      name="email"
                      value={email}
                      onChange={e => setEmail(e.target.value.toLowerCase())}
                    />
                    {submitted && !trimmedEmail && (
                      <Form.Text className="text-danger">
                        {t('Login is required.')}
                      </Form.Text>
                    )}
                  </Form.Group>

                  {requiringPassword &&
                    renderPasswordFields(
                      password,
                      setPassword,
                      passwordRetype,
                      setPasswordRetype,
                      submitted,
                      t
                    )}

                  <Form.Group className="form-group" controlId="formAgreement">
                    <FormCheck>
                      <FormCheck.Input
                        name="agreed"
                        checked={agreed}
                        onChange={() => setAgreed(value => !value)}
                        isInvalid={submitted && !agreed}
                        type="checkbox"
                      />
                      <FormCheck.Label>
                        {t('i-know-and-agree')}&nbsp;
                        <Link to="/term-of-service">
                          {t('Term of Service')}
                        </Link>
                        &nbsp;{t('and')}&nbsp;
                        <Link to="/disclaimer">{t('Disclaimer')}</Link>
                        {t('symbol-period')}
                      </FormCheck.Label>
                    </FormCheck>
                  </Form.Group>

                  <Button variant="primary" type="submit">
                    {t('Register')}
                  </Button>
                  <LoadingImage loading={loading} />
                  <Link to="/login" state={{ from }} className="btn btn-link">
                    {t('I already have an account')}
                  </Link>
                  {!isThirdPartyRegister && (
                    <ThirdPartyLoginContainer>
                      <ThirdPartyLoginButtons from={from} />
                    </ThirdPartyLoginContainer>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Styles>
        </Col>
      </Row>
    </Container>
  );
};

export default RegisterPage;
